'use client'

import { useRouter } from 'next/navigation'
import { FormEvent, MouseEvent, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ArrowLeft } from '@peacecover/icon'
import writerApplyBg from '@/assets/images/writer-apply-bg.png'
import { postProposal } from '@/utils/postProposal'
import { perks, serviceShowcases, steps } from '../_constants/content'
import styles from '../fanfic-landing.module.css'
import { SectionLabel } from './SectionLabel'

function scrollToAnchor(event: MouseEvent<HTMLAnchorElement>, id: string) {
  event.preventDefault()
  document.querySelector(id)?.scrollIntoView({ behavior: 'smooth' })
}

export function FanficLandingPage() {
  const { t, i18n } = useTranslation('fanfic')
  const router = useRouter()
  const [email, setEmail] = useState('')
  const [isSubmitted, setIsSubmitted] = useState(false)

  const [previewMode, setPreviewMode] = useState<'thread' | 'chat'>('thread')
  const [pairingMode, setPairingMode] = useState<'original' | 'converted'>('original')
  const [editorMode, setEditorMode] = useState<'draft' | 'suggested'>('draft')
  const heroBackgroundStyle = {
    backgroundImage: `url(${writerApplyBg.src})`,
  }
  const editorCopy = {
    line1: t('showcase.editor.line1'),
    line2: t('showcase.editor.line2'),
    lead: t('showcase.editor.lead'),
    selected: editorMode === 'draft' ? t('showcase.editor.selectedDraft') : t('showcase.editor.selectedSuggested'),
    line4: t('showcase.editor.line4'),
  }

  useEffect(() => {
    const elements = document.querySelectorAll<HTMLElement>('[data-reveal="true"]')

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add(styles.visible)
          }
        })
      },
      { threshold: 0.1 },
    )

    elements.forEach((element) => observer.observe(element))

    return () => observer.disconnect()
  }, [])

  useEffect(() => {
    const timer = window.setInterval(() => {
      setPreviewMode((prev) => (prev === 'thread' ? 'chat' : 'thread'))
    }, 3200)

    return () => window.clearInterval(timer)
  }, [])

  useEffect(() => {
    const timer = window.setInterval(() => {
      setPairingMode((prev) => (prev === 'original' ? 'converted' : 'original'))
    }, 3600)

    return () => window.clearInterval(timer)
  }, [])

  useEffect(() => {
    const timer = window.setInterval(() => {
      setEditorMode((prev) => (prev === 'draft' ? 'suggested' : 'draft'))
    }, 3400)

    return () => window.clearInterval(timer)
  }, [])

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    try {
      const language = i18n.language
      await postProposal(email, `[씬 작가 함께하기] 참여 신청 (${language})`, 'scene_creator_join', null)
      setIsSubmitted(true)
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error)
    }
  }

  return (
    <div className={styles.page}>
      <header className={`${styles.mobileHeader} sm:hidden`}>
        <button
          type="button"
          onClick={() => router.back()}
        >
          <ArrowLeft size={24} className='text-main-dark-1' />
        </button>
      </header>
      <section className={styles.hero} style={heroBackgroundStyle}>
        <div className={styles.heroBg}>
          <span className={styles.decoStar}>✦</span>
          <span className={styles.decoStar}>✧</span>
          <span className={styles.decoStar}>✦</span>
          <span className={styles.decoStar}>✧</span>
          <span className={styles.decoStar}>✦</span>
          <span className={`${styles.tinyStar} ${styles.tinyStar1}`}>✦</span>
          <span className={`${styles.tinyStar} ${styles.tinyStar2}`}>✦</span>
          <span className={`${styles.tinyStar} ${styles.tinyStar3}`}>✦</span>
          <span className={`${styles.tinyStar} ${styles.tinyStar4}`}>✦</span>
          <span className={`${styles.tinyStar} ${styles.tinyStar5}`}>✦</span>
          <span className={`${styles.tinyStar} ${styles.tinyStar6}`}>✦</span>
          <span className={`${styles.tinyStar} ${styles.tinyStar7}`}>✦</span>
          <span className={`${styles.tinyStar} ${styles.tinyStar8}`}>✦</span>
        </div>

        <div className={styles.heroContent}>
          <div className={styles.badge}>
            {t('hero.badge')}
          </div>

          <h1 className={styles.heroTitle}>
            {t('hero.title')}
          </h1>

          <p className={styles.heroSub}>
            {t('hero.sub')}
            <br />
            {t('hero.subBr')}
          </p>

          <div className={styles.ctaGroup}>
            <a href="#join" className={styles.btnPrimary} onClick={(event) => scrollToAnchor(event, '#join')}>
              <span className={`${styles.btnPrimaryLayer} ${styles.btnPrimaryWhite}`} />
              <span className={`${styles.btnPrimaryLayer} ${styles.btnPrimaryGradient}`} />
              <span className={`${styles.btnPrimaryLayer} ${styles.btnPrimaryGhost}`} />
              <span className={styles.btnPrimaryText}>{t('hero.ctaPrimary')}</span>
            </a>
            <a href="#features" className={styles.btnSecondary} onClick={(event) => scrollToAnchor(event, '#features')}>
              {t('hero.ctaSecondary')}
            </a>
          </div>
        </div>

        <div className={styles.heroScroll}>
          <div className={styles.scrollLine} />
          <span>{t('hero.scroll')}</span>
        </div>
      </section>

      <section className={`${styles.section} ${styles.featuresSection}`} id="features">
        <div className={styles.sectionInner}>
          <SectionLabel text={t('features.sectionLabel')} className={styles.reveal} />
          <h2 className={`${styles.sectionTitle} ${styles.reveal}`} data-reveal="true">
            {t('features.sectionTitle')}
          </h2>

          <div className={styles.showcaseStack}>
            {serviceShowcases.map((item, index) => (
              <article
                key={item.type}
                className={`${styles.showcaseCard} ${styles.reveal}`}
                data-reveal="true"
                style={{ transitionDelay: `${index * 0.1}s` }}
              >
                <div className={styles.showcaseHeader}>
                  <span className={styles.showcaseTypeBadge}>
                    {item.type === 'discover'
                      ? t('showcase.typeBadge.preview')
                      : item.type === 'pairing'
                        ? t('showcase.typeBadge.pairing')
                        : t('showcase.typeBadge.comingSoon')}
                  </span>
                  <h3 className={styles.showcaseTitle}>{t(item.titleKey)}</h3>
                </div>

                {item.type === 'discover' ? (
                  <div className={styles.showcaseUiFrame}>
                    <div className={styles.previewTabs}>
                      <button
                        type="button"
                        className={`${styles.previewTabButton} ${previewMode === 'thread' ? styles.previewTabButtonActive : ''}`}
                        onClick={() => setPreviewMode('thread')}
                      >
                        {t('showcase.discover.tabThread')}
                      </button>
                      <button
                        type="button"
                        className={`${styles.previewTabButton} ${previewMode === 'chat' ? styles.previewTabButtonActive : ''}`}
                        onClick={() => setPreviewMode('chat')}
                      >
                        {t('showcase.discover.tabChat')}
                      </button>
                    </div>

                    <div
                      className={`${styles.switchUi} ${styles.threadUi} ${previewMode === 'thread' ? styles.uiActive : styles.uiInactive}`}
                    >
                      <div className={styles.threadFeed}>
                        <div className={styles.threadItem}>
                          <div className={styles.threadAvatar}>🐿️</div>
                          <div className={styles.threadMeta}>
                            <div className={styles.threadNickname}>{t('showcase.discover.threadNickname')}</div>
                            <div className={styles.threadContent}>
                              {t('showcase.discover.threadContent1')}
                            </div>
                            <div className={styles.threadActions}>
                              <span className={styles.threadActionItem}>♡ 24</span>
                              <span className={styles.threadActionItem}>💬 7</span>
                            </div>
                          </div>
                        </div>
                        <div className={styles.threadItem}>
                          <div className={styles.threadAvatar}>🐿️</div>
                          <div className={styles.threadMeta}>
                            <div className={styles.threadNickname}>{t('showcase.discover.threadNickname')}</div>
                            <div className={styles.threadContent}>
                              {t('showcase.discover.threadContent2')}
                            </div>
                            <div className={styles.threadActions}>
                              <span className={styles.threadActionItem}>♡ 31</span>
                              <span className={styles.threadActionItem}>💬 9</span>
                            </div>
                          </div>
                        </div>
                        <div className={styles.threadCtaRow}>
                          <button type="button" className={styles.threadCtaButton}>
                            {t('showcase.discover.threadCta')}
                          </button>
                        </div>
                      </div>
                    </div>

                    <div
                      className={`${styles.switchUi} ${styles.chatUi} ${previewMode === 'chat' ? styles.uiActive : styles.uiInactive}`}
                    >
                      <div className={styles.chatMessage}>
                        <div className={styles.chatMetaRow}>
                          <span className={styles.chatAvatar}>🐰</span>
                          <span className={styles.chatNickname}>{t('showcase.discover.chatNicknameRabbit')}</span>
                        </div>
                        <div className={styles.chatBubbleLeft}>{t('showcase.discover.chatBubble1')}</div>
                      </div>
                      <div className={`${styles.chatMessage} ${styles.chatMessageRight}`}>
                        <div className={`${styles.chatMetaRow} ${styles.chatMetaRowRight}`}>
                          <span className={styles.chatNickname}>{t('showcase.discover.chatNicknameTurtle')}</span>
                          <span className={styles.chatAvatar}>🐢</span>
                        </div>
                        <div className={styles.chatBubbleRight}>{t('showcase.discover.chatBubble2')}</div>
                      </div>
                      <div className={styles.chatMessage}>
                        <div className={styles.chatMetaRow}>
                          <span className={styles.chatAvatar}>🐰</span>
                          <span className={styles.chatNickname}>{t('showcase.discover.chatNicknameRabbit')}</span>
                        </div>
                        <div className={styles.chatBubbleLeft}>{t('showcase.discover.chatBubble3')}</div>
                      </div>
                      <button type="button" className={styles.chatCtaButton}>
                        {t('showcase.discover.threadCta')}
                      </button>
                    </div>
                  </div>
                ) : null}

                {item.type === 'pairing' ? (
                  <div className={styles.showcaseUiFrame}>
                    <div className={styles.pairingTabs}>
                      <button
                        type="button"
                        className={`${styles.pairingTab} ${pairingMode === 'original' ? styles.pairingTabActive : ''}`}
                        onClick={() => setPairingMode('original')}
                      >
                        {t('showcase.pairing.tabOriginal')}
                      </button>
                      <button
                        type="button"
                        className={`${styles.pairingTab} ${pairingMode === 'converted' ? styles.pairingTabActive : ''}`}
                        onClick={() => setPairingMode('converted')}
                      >
                        {t('showcase.pairing.tabConverted')}
                      </button>
                    </div>
                    <div
                      className={`${styles.pairingLetter} ${styles.pairingLetterSwitch}`}
                      key={pairingMode}
                      data-mode={pairingMode}
                    >
                      <p className={styles.pairingLine}>
                        <span className={`${styles.pairingChangedName} ${styles.pairingChangedFx}`}>
                          {pairingMode === 'original' ? t('showcase.pairing.nameOriginal1') : t('showcase.pairing.nameConverted1')}
                        </span>
                        {t('showcase.pairing.line1Rest')}
                      </p>
                      <p className={styles.pairingLine}>
                        {t('showcase.pairing.line2Prefix')}
                        <span className={`${styles.pairingChangedName} ${styles.pairingChangedFx}`}>
                          {pairingMode === 'original' ? t('showcase.pairing.nameOriginal2') : t('showcase.pairing.nameConverted2')}
                        </span>
                        {t('showcase.pairing.line2Suffix')}
                      </p>
                      <p className={styles.pairingLine}>
                        <span className={`${styles.pairingChangedName} ${styles.pairingChangedFx}`}>
                          {pairingMode === 'original' ? t('showcase.pairing.pronounOriginal') : t('showcase.pairing.pronounConverted')}
                        </span>
                        {t("showcase.pairing.line3Prefix")}
                        <span className={`${styles.pairingChangedName} ${styles.pairingChangedFx}`}>
                          {pairingMode === 'original' ? t('showcase.pairing.nameOriginal1') : t('showcase.pairing.nameConverted1')}
                        </span>
                        {t('showcase.pairing.line3Suffix')}
                      </p>
                      <p className={styles.pairingLine}>
                        {t('showcase.pairing.line4Prefix')}
                        <span className={`${styles.pairingChangedName} ${styles.pairingChangedFx}`}>
                          {pairingMode === 'original' ? t('showcase.pairing.nameOriginal1') : t('showcase.pairing.nameConverted1')}
                        </span>
                        {t('showcase.pairing.line4Suffix')}
                      </p>
                      <p className={styles.pairingLine}>
                        {t('showcase.pairing.line5Prefix')}
                        <span className={`${styles.pairingChangedName} ${styles.pairingChangedFx}`}>
                          {pairingMode === 'original' ? t('showcase.pairing.nameOriginal2') : t('showcase.pairing.nameConverted2')}
                        </span>
                        {t('showcase.pairing.line5Suffix')}
                      </p>
                    </div>
                  </div>
                ) : null}

                {item.type === 'editor' ? (
                  <div className={styles.showcaseUiFrame}>
                    <div className={styles.editorMockup}>
                      <div className={styles.editorTopbar}>
                        <div className={styles.editorTitleGroup}>
                          <div className={styles.editorWindowDots}>
                            <span className={styles.toolDot} />
                            <span className={styles.toolDot} />
                            <span className={styles.toolDot} />
                          </div>
                          <div className={styles.editorDocName}>{t('showcase.editor.docName')}</div>
                        </div>
                        <div className={styles.editorTopActions}>
                          <button type="button" className={styles.editorIconButton} aria-label={t('showcase.editor.ariaSave')}>
                            <svg className={styles.editorIcon} viewBox="0 0 24 24" aria-hidden="true">
                              <path d="M5 4h11l3 3v13H5V4zm2 2v4h8V6H7zm0 8v4h10v-4H7z" />
                            </svg>
                          </button>
                          <button type="button" className={styles.editorIconButton} aria-label={t('showcase.editor.ariaText')}>
                            <span className={styles.editorIconText}>T</span>
                          </button>
                          <button type="button" className={styles.editorIconButton} aria-label={t('showcase.editor.ariaShare')}>
                            <svg className={styles.editorIcon} viewBox="0 0 24 24" aria-hidden="true">
                              <path d="M16 7a3 3 0 1 0-2.83-4H13a3 3 0 0 0 .17 1L8.9 6.38a3 3 0 0 0-3.8.95A3 3 0 1 0 5 12a3 3 0 0 0 3.9-.38l4.25 2.35A3 3 0 0 0 13 15a3 3 0 1 0 1.17-2.35L9.93 10.3A3 3 0 0 0 10 9.5c0-.27-.04-.54-.1-.8l4.25-2.35A3 3 0 0 0 16 7z" />
                            </svg>
                          </button>
                        </div>
                      </div>

                      <div className={styles.editorCanvas}>
                        <div className={styles.editorBody}>
                          <p className={styles.editorBodyLine}>{editorCopy.line1}</p>
                          <p className={styles.editorBodyLine}>{editorCopy.line2}</p>
                          <p className={`${styles.editorBodyLine} ${styles.editorBodyLineSelected}`}>
                            {editorCopy.lead}
                            <span className={styles.editorSelectedWrap}>
                              <span
                                key={editorMode}
                                className={`${styles.editorSelectedText} ${styles.editorSelectedTextSwap}`}
                              >
                                {editorCopy.selected}
                              </span>
                              <span
                                className={`${styles.editorSuggestionChipGroup} ${editorMode === 'draft' ? styles.editorSuggestionChipGroupSingle : ''
                                  }`}
                              >
                                {editorMode === 'suggested' ? (
                                  <>
                                    <button
                                      type="button"
                                      className={`${styles.editorSuggestionAction} ${styles.editorSuggestionReset}`}
                                      onClick={() => setEditorMode('draft')}
                                    >
                                      {t('showcase.editor.actionReset')}
                                    </button>
                                    <span className={styles.editorSuggestionDivider}>|</span>
                                    <button
                                      type="button"
                                      className={`${styles.editorSuggestionAction} ${styles.editorSuggestionApply}`}
                                      onClick={() => setEditorMode('suggested')}
                                    >
                                      {t('showcase.editor.actionApply')}
                                    </button>
                                  </>
                                ) : (
                                  <button
                                    type="button"
                                    className={`${styles.editorSuggestionAction} ${styles.editorSuggestionApply}`}
                                    onClick={() => setEditorMode('suggested')}
                                  >
                                    {t('showcase.editor.actionApply')}
                                  </button>
                                )}
                              </span>
                            </span>
                          </p>
                          <p className={styles.editorBodyLine}>
                            {editorCopy.line4}
                            <span className={styles.editorCaret} aria-hidden="true" />
                          </p>
                        </div>
                      </div>

                    </div>
                  </div>
                ) : null}

                {item.descriptionPointKeys?.length ? (
                  <ul className={styles.showcaseDescList}>
                    {item.descriptionPointKeys.map((pointKey) => (
                      <li key={`${item.type}-${pointKey}`} className={styles.showcaseDescItem}>
                        {t(pointKey)}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className={styles.showcaseDesc}>{t(item.descriptionKey)}</p>
                )}
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.howSection} id="how">
        <div className={styles.sectionInner}>
          <SectionLabel text={t('how.sectionLabel')} className={styles.reveal} />
          <h2 className={`${styles.sectionTitle} ${styles.reveal}`} data-reveal="true">
            {t('how.sectionTitle')}
          </h2>
          <p className={`${styles.sectionDesc} ${styles.reveal}`} data-reveal="true">
            {t('how.sectionDesc')}
          </p>

          <div className={styles.steps}>
            {steps.map((step, index) => (
              <article
                key={step.number}
                className={`${styles.step} ${styles.reveal}`}
                data-reveal="true"
                style={{ transitionDelay: `${index * 0.1}s` }}
              >
                <div className={styles.stepNum}>{step.number}</div>
                <h3 className={styles.stepTitle}>{t(step.titleKey)}</h3>
                <p className={styles.stepDesc}>{t(step.descriptionKey)}</p>
                {index < steps.length - 1 ? <div className={styles.stepArrow}>→</div> : null}
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.joinSection} id="join">
        <div className={styles.sectionInner}>
          <SectionLabel text={t('join.sectionLabel')} centered className={styles.reveal} />
          <h2 className={`${styles.sectionTitle} ${styles.reveal}`} data-reveal="true">
            {t('join.sectionTitle')}
          </h2>
          <p className={`${styles.sectionDesc} ${styles.reveal}`} data-reveal="true">
            {t('join.sectionDesc')}
          </p>

          <div className={`${styles.joinPerks} ${styles.reveal}`} data-reveal="true">
            {perks.map((perk) => (
              <article key={perk.titleKey} className={styles.perk}>
                <div className={styles.perkIconCircle}>
                  <span className={styles.perkIcon}>{perk.icon}</span>
                </div>
                <div className={styles.perkContent}>
                  <h3 className={styles.perkTitle}>{t(perk.titleKey)}</h3>
                  <p className={styles.perkDesc}>{t(perk.descriptionKey)}</p>
                </div>
              </article>
            ))}
          </div>

          <form className={`${styles.formRow} ${styles.reveal}`} data-reveal="true" onSubmit={handleSubmit}>
            <input
              type="email"
              className={styles.formInput}
              placeholder={t('join.placeholder')}
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
            <button type="submit" className={`${styles.btnSubmit} ${isSubmitted ? styles.submitted : ''}`}>
              {isSubmitted ? t('join.submitted') : t('join.submit')}
            </button>
          </form>
          <p className={`${styles.formNote} ${styles.reveal}`} data-reveal="true">
            {t('join.privacyNote')}
          </p>
        </div>
      </section>

    </div>
  )
}
